import React, { useMemo } from "react";
import { BackToTopPill } from "@/components/BackToTopPill";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { PageLoader } from "@/components/loading/PageLoader";
import RoleGuard from "@/components/auth/RoleGuard";
import { TrendingUp, AlertTriangle, DollarSign, BarChart3, Ship, Gauge, Warehouse } from "lucide-react";
import { DateRange } from "react-day-picker";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { PageActionBar } from "@/components/layout/PageActionBar";
import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useRole } from "@/hooks/use-role";
import { useOrganizationMutations } from "@/features/organization/hooks/useOrganizationMutations";
import { useOrgRoleSurfaces } from "@/hooks/useOrgRoleSurfaces";
import {
  useUnmatchedSkus,
  useOverviewSummary,
  ReportingUnmatchedSkus,
  ReportingPeriodBar,
  OverviewTab,
  PerformanceTab,
  ConsumptionTab,
  ThreePlUsageTab,
  StockValueTab,
  StockOnOrderTab,
} from "@/features/reporting";
import { OverviewTabSkeleton } from "@/features/reporting/components/ReportingTabSkeletons";
import { buildPresetReportingPeriod } from "@/features/reporting/lib/reporting-period";
import {
  parseReportingDateRange,
  resolveReportingView,
  toReportingDateParam,
} from '@/features/reporting/lib/reporting-url-state';
import { updateSearchParams } from '@/lib/search-params';

const Reporting: React.FC = () => {
  const { t } = useTranslation('inventory');
  const { selectedLocation } = useLocation();
  const { organization, isLoading: organizationLoading } = useOrganization();
  const { isAdmin } = useRole();
  const { backfillUnmatchedSkus } = useOrganizationMutations();
  // Same cache entry as the sidebar (its options copy ProtectedRoute's), so no new request.
  const { isPureClient, isLoading: surfacesLoading } = useOrgRoleSurfaces({
    refetchOnMount: false,
    retryOnMount: false,
  });

  // A pure client's Reports is its 3PL usage bill alone, titled like its
  // sidebar item. No title until the org's shape is known, so the header never
  // flips from one to the other.
  const [pageTitle, pageSubtitle] = surfacesLoading
    ? [null, null]
    : isPureClient
      ? [t('reporting.threePlUsage.tab'), t('reporting.threePlUsage.clientSubtitle')]
      : [t('reporting.title'), t('reporting.subtitle')];
  usePageTitle(pageTitle, pageSubtitle);

  // Tab state — Overview is the default. Synced to ?tab= so deep links and the
  // "Back to reporting" link from the blank-consumption page land on the right
  // tab. A pure client always gets 3PL usage, whatever ?tab= says.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = resolveReportingView(searchParams.get('tab'), isPureClient);
  const handleTabChange = (v: string) => {
    setSearchParams(
      (prev) => {
        return updateSearchParams(prev, { tab: v });
      },
      { replace: true },
    );
  };

  // Period control — drives Overview, Performance and Consumption. Stock Value
  // has its own as-of + waterfall-range controls; Unmatched uses this period.
  const periodParam = searchParams.get('period');
  const dateFilter =
    periodParam === '7' ||
    periodParam === '30' ||
    periodParam === '90' ||
    periodParam === '180' ||
    periodParam === 'custom'
      ? periodParam
      : '30';
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const dateRange = useMemo(
    () => parseReportingDateRange(fromParam, toParam),
    [fromParam, toParam],
  );

  const handlePeriodChange = (period: string) => {
    setSearchParams(
      (previous) => {
        return updateSearchParams(previous, {
          period: period === '30' ? null : period,
          from: period === 'custom' ? previous.get('from') : null,
          to: period === 'custom' ? previous.get('to') : null,
        });
      },
      { replace: true },
    );
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setSearchParams(
      (previous) => {
        return updateSearchParams(previous, {
          from: range?.from ? toReportingDateParam(range.from) : null,
          to: range?.to ? toReportingDateParam(range.to) : null,
        });
      },
      { replace: true },
    );
  };

  const { startDate, endDate } = useMemo(() => {
    let start: Date;
    let end: Date;
    if (dateFilter === "custom" && dateRange?.from && dateRange?.to) {
      start = new Date(dateRange.from);
      start.setHours(0, 0, 0, 0);
      end = new Date(dateRange.to);
      end.setHours(23, 59, 59, 999);
    } else {
      const days = parseInt(dateFilter, 10) || 30;
      const preset = buildPresetReportingPeriod(days);
      start = preset.startDate;
      end = preset.endDate;
    }
    return { startDate: start, endDate: end };
  }, [dateFilter, dateRange]);

  // Location from top nav — null means all locations.
  const locationId = selectedLocation?.id ?? null;
  const organizationId = organization?.id ?? null;

  // Custom range isn't ready to query until both ends are picked.
  const periodReady = dateFilter !== 'custom' || Boolean(dateRange?.from && dateRange?.to);

  // Overview summary — single round-trip, gated to the Overview tab.
  const overview = useOverviewSummary({
    organizationId,
    startDate,
    endDate,
    locationId,
    enabled: viewMode === 'overview' && periodReady,
  });

  // Unmatched SKUs — preserved tab, unchanged behaviour.
  const { unmatchedSkus, loading: unmatchedLoading } = useUnmatchedSkus({
    organizationId,
    startDate,
    endDate,
    locationId,
    enabled: !!organizationId && viewMode === 'unmatched',
  });

  // organization.backfillUnmatchedSkus is adminProcedure, so this stays an Admin check.
  const canBackfillUnmatched = isAdmin() && !!organizationId;
  const handleBackfillUnmatched = () => {
    if (!canBackfillUnmatched) return;
    backfillUnmatchedSkus.mutate({ locationId });
  };

  const showPeriodBar =
    viewMode === 'overview' ||
    viewMode === 'performance' ||
    viewMode === 'consumption' ||
    viewMode === '3pl-usage';

  const periodBar = (
    <ReportingPeriodBar
      preset={dateFilter}
      onPresetChange={handlePeriodChange}
      range={dateRange}
      onRangeChange={handleDateRangeChange}
    />
  );
  const threePlUsageTab = (
    <ThreePlUsageTab
      organizationId={organizationId}
      startDate={startDate}
      endDate={endDate}
      locationId={locationId}
      active={viewMode === '3pl-usage' && periodReady}
    />
  );

  return (
    <RoleGuard requireReportingAccess>
      <div className="flex flex-col">
        {/* The org's shape decides which views exist: never paint the wrong set first. */}
        {organizationLoading || !organization || surfacesLoading ? (
          <PageLoader pool="reporting" />
        ) : (
          <main id="main" className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
            {isPureClient ? (
              // One view, so no tab bar: the bill under its period, like the 3PL usage tab.
              <>
                {periodBar}
                {threePlUsageTab}
              </>
            ) : (
              <Tabs value={viewMode} onValueChange={handleTabChange}>
                <PageActionBar
                  ariaLabel="Reporting views"
                  left={
                    <TabsList className="flex flex-wrap h-auto">
                      <TabsTrigger value="overview" className="flex items-center gap-1.5 text-xs">
                        <BarChart3 className="h-3.5 w-3.5" />
                        {t('reporting.overview', 'Overview')}
                      </TabsTrigger>
                      <TabsTrigger value="performance" className="flex items-center gap-1.5 text-xs">
                        <TrendingUp className="h-3.5 w-3.5" />
                        {t('reporting.performance')}
                      </TabsTrigger>
                      <TabsTrigger value="consumption" className="flex items-center gap-1.5 text-xs">
                        <Gauge className="h-3.5 w-3.5" />
                        {t('reporting.consumption', 'Consumption')}
                      </TabsTrigger>
                      <TabsTrigger value="3pl-usage" className="flex items-center gap-1.5 text-xs">
                        <Warehouse className="h-3.5 w-3.5" />
                        {t('reporting.threePlUsage.tab')}
                      </TabsTrigger>
                      <TabsTrigger value="costs" className="flex items-center gap-1.5 text-xs">
                        <DollarSign className="h-3.5 w-3.5" />
                        {t('reporting.stockValue')}
                      </TabsTrigger>
                      <TabsTrigger value="on-order" className="flex items-center gap-1.5 text-xs">
                        <Ship className="h-3.5 w-3.5" />
                        {t('reporting.onOrder', 'On Order')}
                      </TabsTrigger>
                      <TabsTrigger value="unmatched" className="flex items-center gap-1.5 text-xs">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {t('reporting.unmatchedSkus')}
                      </TabsTrigger>
                    </TabsList>
                  }
                />

                {/* Period control — the views with a date range model. */}
                {showPeriodBar && <div className="mt-6">{periodBar}</div>}

                <TabsContent value="overview" className="mt-0">
                  {!periodReady ? (
                    <OverviewTabSkeleton />
                  ) : (
                    <OverviewTab
                      loading={overview.isLoading}
                      data={overview.data}
                      onNavigate={handleTabChange}
                    />
                  )}
                </TabsContent>

                <TabsContent value="performance" className="mt-0">
                  <PerformanceTab
                    organizationId={organizationId}
                    startDate={startDate}
                    endDate={endDate}
                    locationId={locationId}
                    active={viewMode === 'performance' && periodReady}
                  />
                </TabsContent>

                <TabsContent value="consumption" className="mt-0">
                  <ConsumptionTab
                    organizationId={organizationId}
                    startDate={startDate}
                    endDate={endDate}
                    locationId={locationId}
                    locationName={selectedLocation?.name ?? null}
                    active={viewMode === 'consumption' && periodReady}
                  />
                </TabsContent>

                <TabsContent value="3pl-usage" className="mt-0">
                  {threePlUsageTab}
                </TabsContent>

                <TabsContent value="costs" className="mt-6">
                  <StockValueTab
                    organizationId={organizationId}
                    locationId={locationId}
                    active={viewMode === 'costs'}
                  />
                </TabsContent>

                <TabsContent value="on-order" className="mt-6">
                  <StockOnOrderTab
                    organizationId={organizationId}
                    locationId={locationId}
                    active={viewMode === 'on-order'}
                  />
                </TabsContent>

                <TabsContent value="unmatched" className="mt-6">
                  <ReportingUnmatchedSkus
                    loading={unmatchedLoading}
                    data={unmatchedSkus}
                    shopifyDomain={organization?.shopify_domain}
                    canBackfill={canBackfillUnmatched}
                    backfillLoading={backfillUnmatchedSkus.isPending}
                    backfillDisabled={!selectedLocation}
                    onBackfill={handleBackfillUnmatched}
                  />
                </TabsContent>
              </Tabs>
            )}
          </main>
        )}
      </div>
      <BackToTopPill />
    </RoleGuard>
  );
};

export default Reporting;
